import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { Icon } from '@agency/ui';
import type { NavItem } from './nav.ts';

/** Command palette skeleton: jump to permitted destinations. Combobox + listbox semantics with focus trap and return. */
export function CommandPalette({ open, onClose, items }: { open: boolean; onClose: () => void; items: NavItem[] }) {
  const [q, setQ] = useState('');
  const [active, setActive] = useState(0);
  const nav = useNavigate();
  const input = useRef<HTMLInputElement>(null);
  const returnTo = useRef<Element | null>(null);
  const listId = useId();
  const results = useMemo(() => items.filter((i) => i.label.toLowerCase().includes(q.trim().toLowerCase())), [items, q]);

  useEffect(() => {
    if (!open) return;
    returnTo.current = document.activeElement;
    setQ(''); setActive(0);
    requestAnimationFrame(() => input.current?.focus());
    return () => (returnTo.current as HTMLElement | null)?.focus?.();
  }, [open]);
  useEffect(() => setActive(0), [q]);
  if (!open) return null;

  const go = (i: NavItem | undefined) => { if (i) { nav(i.to); onClose(); } };
  return (
    <div className="palette-root" onKeyDown={(e) => {
      if (e.key === 'Escape') { e.stopPropagation(); onClose(); }
      else if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => Math.min(a + 1, results.length - 1)); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
      else if (e.key === 'Enter') { e.preventDefault(); const live = (input.current?.value ?? q).trim().toLowerCase(); go(items.filter((i) => i.label.toLowerCase().includes(live))[active]); }
      else if (e.key === 'Tab') { e.preventDefault(); }
    }}>
      <div className="ag-scrim" onClick={onClose} aria-hidden="true" />
      <div className="palette" role="dialog" aria-modal="true" aria-label="Command palette">
        <div className="palette__field">
          <Icon name="search" size={20} />
          <input ref={input} autoFocus value={q} onChange={(e) => setQ(e.target.value)} role="combobox" aria-expanded="true" aria-controls={listId} aria-activedescendant={results[active] ? `${listId}-${active}` : undefined} aria-label="Search destinations" placeholder="Go to…" autoComplete="off" />
        </div>
        <ul id={listId} role="listbox" aria-label="Destinations" className="palette__list">
          {results.map((r, i) => (
            <li key={r.to} id={`${listId}-${i}`} role="option" aria-selected={i === active} className="palette__item" onMouseEnter={() => setActive(i)} onClick={() => go(r)}>
              <Icon name={r.icon} /> <span>{r.label}</span> <span className="palette__hint">{r.group} · {r.keys}</span>
            </li>
          ))}
          {results.length === 0 ? <li className="palette__empty" role="presentation">No destination matches “{q}”.</li> : null}
        </ul>
      </div>
    </div>
  );
}
